import { cn } from 'helpers/cn';
import { ConfirmDialog } from '@/components/Dialogs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ActivationKind } from '@/state/experiments/useActivationTargeting';

type ActivationRuleDialogProps = {
  open: boolean;
  kind: ActivationKind;
  value: string;
  onValueChange: (next: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

const FIELD_CLASS = 'border-line bg-field text-foreground rounded-lg font-mono';

const COPY: Record<ActivationKind, { title: string; label: string; placeholder: string; help: string[] }> = {
  event: {
    title: 'Add activation event',
    label: 'Event name',
    placeholder: 'checkout-started',
    help: [
      'The experiment only runs for visitors whose site has sent this event.',
      "Send it with PertentoQ.push(['activate', 'name']) or dataLayer.push({ event: 'pertento_activate', pertento_event: 'name' }).",
      'Once sent, the event counts for that visitor on later visits too.',
    ],
  },
  selector: {
    title: 'Add activation selector',
    label: 'CSS selector',
    placeholder: '.hejhej',
    help: [
      'The experiment only runs on pages where an element matches this selector.',
      'Use .class, #id or [data-attribute]. It is checked on every page, so it does not carry over.',
    ],
  },
};

export const ActivationRuleDialog = ({
  open,
  kind,
  value,
  onValueChange,
  onConfirm,
  onCancel,
}: ActivationRuleDialogProps) => {
  const copy = COPY[kind];

  return (
    <ConfirmDialog
      open={open}
      title={copy.title}
      onConfirm={onConfirm}
      onCancel={onCancel}
      disabled={!value.trim()}
      confirmLabel="Add"
    >
      <div className="flex flex-col gap-4">
        <div className="supporting flex flex-col gap-2">
          {copy.help.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <Label className="micro-label">{copy.label}</Label>
          <Input
            value={value}
            onChange={(ev) => onValueChange(ev.target.value)}
            placeholder={copy.placeholder}
            className={cn(FIELD_CLASS)}
          />
        </div>
      </div>
    </ConfirmDialog>
  );
};
